import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingTablePage from "../LoadingTablePage";

import toast from "react-hot-toast";
import { useAuth } from "../../providers/AuthContext";

import { createOrganization } from "../../api/Resources"; 
import { CreatableSelectRow, InputRow, SwitchRow } from "../../components/Rows";
import { FormCard, FormGrid } from "../../components/FormCard";

const joinEmails = (values) =>
    (values || [])
        .map((email) => String(email ?? "").trim())
        .filter(Boolean)
        .join(";");

const CreateOrganizationPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);

    const [org, setOrg] = useState({
        organizationName: "",
        companyName: "",
        contactMail: "",
        supportPhone: "",
        email: [],
        isCpms: false,
    });

    const handleInputChange = (key, value) => {
        setHasChanges(true);
        setOrg((prev) => ({ ...prev, [key]: value }));
        setInvalidFields((prev) => prev.filter((f) => f !== key));
    };

    const handleCreate = async () => {
        const toastId = toast.loading(t("toast.creating", { entity: t("entity.organization") }));
        setLoading(true);

        const required = ["organizationName", "supportPhone"];
        const missing = required.filter((k) => !org[k]?.toString().trim());
        if (!org.email.some((email) => String(email ?? "").trim())) {
            missing.push("email");
        }
        setInvalidFields(missing);

        if (missing.length > 0) {
            toast.error(t("common.pleaseFillRequired"), { id: toastId });
            setLoading(false);
            return;
        }

        try {
            const token = await getAccessToken();
            const payload = {
                organizationName: org.organizationName.trim(),
                companyName: org.companyName?.trim() || null,
                contactMail: org.contactMail?.trim() || null,
                supportPhone: org.supportPhone.trim(),
                email: joinEmails(org.email) || null,
                isCpms: org.isCpms === true,
            };

            const created = await createOrganization(payload, token);

            toast.success(t("toast.created", { entity: t("entity.organization") }), { id: toastId });

            const id =
                created?.organizationPublicId ?? created?.publicId ?? created?.id;
            if (id) navigate(`/organization/${id}`);
            else navigate("/organizations");
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("toast.failedCreate", { entity: t("entity.organization") }), {
                id: toastId,
            });
        } finally {
            setLoading(false);
        }
    };

    const navigateBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/organizations");
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("nav.organizations")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={navigateBack} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4">
                                {t("pages.createOrganization")}
                            </h2>

                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={navigateBack}>
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges || loading}
                                    onClick={handleCreate}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <FormCard >
                                <FormGrid>
                                    <SwitchRow
                                        label={t("form.cpmsOrganization")}
                                        checked={org.isCpms === true}
                                        onChange={(checked) => handleInputChange("isCpms", checked)}
                                    />

                                    <InputRow
                                        label={t("form.organizationName")}
                                        value={org.organizationName}
                                        onChange={(v) => handleInputChange("organizationName", v)}
                                        invalid={invalidFields.includes("organizationName")}
                                        maxLength={50}
                                    />

                                    <InputRow
                                        label={t("form.companyName")}
                                        value={org.companyName}
                                        onChange={(v) => handleInputChange("companyName", v)}
                                        invalid={false}
                                        maxLength={200}
                                    />

                                    <InputRow
                                        label={t("form.contactMail")}
                                        value={org.contactMail}
                                        onChange={(v) => handleInputChange("contactMail", v)}
                                        invalid={false}
                                        maxLength={255}
                                    />

                                    <InputRow
                                        label={t("form.supportPhone")}
                                        value={org.supportPhone}
                                        onChange={(v) => handleInputChange("supportPhone", v)}
                                        invalid={invalidFields.includes("supportPhone")}
                                        maxLength={40}
                                    />

                                    <CreatableSelectRow
                                        label={t("form.supportEmails")}
                                        value={org.email}
                                        options={(org.email || []).map((email) => ({
                                            label: email,
                                            value: email,
                                        }))}
                                        onChange={(v) => handleInputChange("email", v || [])}
                                        invalid={invalidFields.includes("email")}
                                        isMulti={true}
                                        placeholder={t("form.typeSupportEmail")}
                                    />
                                </FormGrid>
                            </FormCard>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default CreateOrganizationPage;
